import React from "react";

const PrivacyPolicyAppPA = () => {
  const sections = [
    {
      number: "1",
      title: "Information We Collect",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">
            Our application collects minimal information necessary to provide a better user experience.
            When you sign in using <strong>Google Sign-In</strong>, we may receive:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Your name</li>
            <li>Your email address</li>
            <li>Your Google account profile information</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            This information is used only to authenticate your account and personalize the app experience.
            We do <strong>not</strong> collect sensitive personal information.
          </p>
        </>
      ),
    },
    {
      number: "2",
      title: "How We Use the Information",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">
            The information we collect may be used to:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Enable secure Google Sign-In authentication</li>
            <li>Provide personalized user experience</li>
            <li>Save user preferences</li>
            <li>Improve app performance and features</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            We do <strong>not</strong> sell, rent, or share personal information with third parties.
          </p>
        </>
      ),
    },
    {
      number: "3",
      title: "Data Storage and Security",
      content: (
        <p className="text-gray-600 leading-relaxed">
          We take reasonable security measures to protect your information. User data may be stored
          securely using trusted cloud services such as <strong>Firebase</strong> or other secure
          infrastructure providers. We implement appropriate safeguards to protect against
          unauthorized access or misuse.
        </p>
      ),
    },
    {
      number: "4",
      title: "Third-Party Services",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">
            Our app may use trusted third-party services including:
          </p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Google Sign-In</li>
            <li>Google Play Services</li>
            <li>Firebase services (analytics or authentication)</li>
          </ul>
          <p className="text-gray-600 leading-relaxed mt-3">
            These services may collect limited technical information according to their own privacy policies.
          </p>
        </>
      ),
    },
    {
      number: "5",
      title: "Children's Privacy",
      content: (
        <p className="text-gray-600 leading-relaxed">
          Our app is intended for general audiences and does <strong>not</strong> knowingly collect
          personal information from children under <strong>13 years of age</strong>. If we become
          aware that such information has been collected unintentionally, we will take steps to
          delete it promptly.
        </p>
      ),
    },
    {
      number: "6",
      title: "User Control",
      content: (
        <>
          <p className="text-gray-600 leading-relaxed mb-3">Users can:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-1 ml-2">
            <li>Sign out of the app at any time</li>
            <li>Remove the app and its stored data by uninstalling the application</li>
          </ul>
        </>
      ),
    },
    {
      number: "7",
      title: "Changes to This Policy",
      content: (
        <p className="text-gray-600 leading-relaxed">
          We may update this Privacy Policy from time to time. Any updates will be reflected
          within the app or the app listing. We encourage you to review this policy periodically.
        </p>
      ),
    },
    {
      number: "8",
      title: "Contact Us",
      content: (
        <p className="text-gray-600 leading-relaxed">
          If you have any questions about this Privacy Policy, please contact us at:{" "}
          <a
            href="mailto:projects@datadna.in"
            className="text-blue-600 hover:text-blue-800 underline font-medium break-all"
          >
            projects@datadna.in
          </a>
        </p>
      ),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-500 py-14 px-4 text-center">
        <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
          Privacy Policy
        </h1>
        <p className="text-blue-100 text-base sm:text-lg font-medium">
          Our Panchangam App
        </p>
        <p className="text-blue-200 text-sm mt-2">Last Updated: June 2026</p>
      </div>

      {/* Intro Card */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 border-l-4 border-blue-500">
          <p className="text-gray-700 text-base sm:text-lg leading-relaxed">
            This Privacy Policy explains how the <strong>Our Panchangam App</strong> collects, uses,
            and protects user information. We are committed to safeguarding your privacy and
            ensuring transparency in how we handle your data.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        {sections.map((section) => (
          <div
            key={section.number}
            className="p-6 sm:p-8 hover:shadow-md transition-shadow duration-200"
          >
            <div className="flex items-start gap-4">
              {/* Number Badge */}
              <div className="flex-shrink-0 w-10 h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm">
                {section.number}
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3">
                  {section.title}
                </h2>
                {section.content}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="bg-blue-50  p-6 text-center border">
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} DATADNA · Our Panchangam App · All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyAppPA;
